import { useEffect, useMemo, useState, type ReactNode } from "react";
import watchwicketLogo from "../assets/watchwicket-logo-cropped.png";
import "./HomePage.css";
import { useAuth } from "../contexts/AuthContext";
import { listMatchesFromDb, type MatchSummary } from "../store/supabaseMatches";
import { selectFeaturedMatch, categoryToMyMatchesTab, type FeaturedCategory } from "../utils/featuredMatch";
import { getInningsScoreFor, getMatchResultText, getFormatLabel } from "../utils/featuredCardHelpers";

type NavTarget = "home" | "myMatches" | "newMatch" | "leagues" | "support" | "players" | "teams";
type MyMatchesTab = "ongoing" | "upcoming" | "completed" | null;

interface HomePageProps {
  onOpenMenu: () => void;
  onOpenAccount?: () => void;
  onNavigateToStats?: () => void;
  onStartMatch?: () => void;
  onNavigate?: (screen: NavTarget) => void;
  onResumeMatch?: (id: string) => void;
  onViewAllMatches?: (tab: MyMatchesTab) => void;
}

interface SummaryRow {
  id: string;
  teamAName: string;
  teamBName: string;
  teamALogoUrl: string | null;
  teamBLogoUrl: string | null;
  status: string;
  matchData: any;
  updatedAt: string;
  completedAt: string | null;
  scheduledAt: string | null;
  venue: string | null;
}

function greetingFor(date: Date): string {
  const hour = date.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}



export default function HomePage({ onOpenMenu, onOpenAccount, onStartMatch, onNavigate, onResumeMatch, onViewAllMatches }: HomePageProps) {
  const { user, profile } = useAuth();
  const [matches, setMatches] = useState<SummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => new Date());

  const firstName = profile?.full_name?.split(" ")[0] || user?.email?.split("@")[0] || "there";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listMatchesFromDb();
        if (!cancelled) setMatches(rows as unknown as SummaryRow[]);
      } catch (error) {
        console.warn("[HomePage] load matches failed", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const featured = useMemo(
    () => selectFeaturedMatch(matches as unknown as MatchSummary[], now),
    [matches, now],
  );
  const featuredCategory: FeaturedCategory = featured?.category ?? "empty";
  const liveMatch = featured?.category === "live" ? featured.match as unknown as SummaryRow : undefined;
  const upcomingMatch = featured?.category === "upcoming" ? featured.match as unknown as SummaryRow : undefined;
  const completedMatch = featured?.category === "completed" ? featured.match as unknown as SummaryRow : undefined;
  const liveScore = getInningsScoreFor(liveMatch?.matchData);
  const dateLabel = now.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "2-digit", year: "numeric" });
  const timeLabel = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });

  const handleViewAll = () => {
    const tab = categoryToMyMatchesTab(featuredCategory);
    if (onViewAllMatches) {
      onViewAllMatches(tab);
    } else {
      onNavigate?.("myMatches");
    }
  };

  const handleOpenFeatured = () => {
    if (!featured) return;
    if (featured.category === "live" && liveMatch) onResumeMatch?.(liveMatch.id);
    else if (featured.category === "upcoming" && upcomingMatch) onResumeMatch?.(upcomingMatch.id);
    else if (featured.category === "completed" && completedMatch) onResumeMatch?.(completedMatch.id);
  };

  return (
    <div className="watchwicket-home">
      <div className="watchwicket-home__content">
        <section className="watchwicket-hero" aria-label="WatchWicket home hero">
          <div className="watchwicket-hero__stadium" aria-hidden="true" />
          <div className="watchwicket-hero__grain" aria-hidden="true" />
          <div className="watchwicket-hero__scrim" aria-hidden="true" />
          <div className="watchwicket-hero__inner">
            <header className="watchwicket-home__header">
              <button className="watchwicket-icon-button" onClick={onOpenMenu} aria-label="Open menu">
                <MenuIcon />
              </button>
              <img className="watchwicket-home__logo" src={watchwicketLogo} alt="WatchWicket ScoreBox" />
              <button className="watchwicket-profile" aria-label="Open profile" onClick={onOpenAccount}>
                {firstName.slice(0, 2).toUpperCase()}
              </button>
            </header>
            <div className="watchwicket-hero__copy">
              <p className="watchwicket-eyebrow">{greetingFor(now)},</p>
              <h1>{firstName}</h1>
              <p className="watchwicket-hero__tagline">Cricket Brings People Together</p>
              <div className="watchwicket-hero__underline" />
            </div>
          </div>
        </section>

        <main className="watchwicket-home__main">
          <section className="watchwicket-info-strip" aria-label="Current local details">
            <span><CalendarIcon />{dateLabel}</span>
            <i />
            <span><ClockIcon />{timeLabel}</span>
            <i />
            <span><WeatherIcon />15°C</span>
          </section>

          <section className="watchwicket-card-grid" aria-label="ScoreBox sections">
            <DashboardCard
              title="Matches"
              description={<>Live, upcoming<br />&amp; completed</>}
              image="/watchwicket-card-matches.webp"
              tone="blue"
              icon={<CalendarCheckIcon />}
              onClick={() => onNavigate?.("myMatches")}
            />
            <DashboardCard
              title="Teams"
              description={<>Manage squads<br />&amp; players</>}
              image="/watchwicket-card-teams.webp"
              tone="green"
              icon={<TeamIcon />}
              onClick={() => onNavigate?.("teams")}
            />
            <DashboardCard
              title="Player Profiles"
              description={<>Stats, records<br />&amp; rankings</>}
              image="/watchwicket-card-players.webp"
              tone="gold"
              icon={<ChartIcon />}
              onClick={() => onNavigate?.("players")}
            />
            <DashboardCard
              title="Leagues"
              description={<>Tables, fixtures<br />&amp; results</>}
              image="/watchwicket-card-leagues.webp"
              tone="purple"
              icon={<TrophyIcon />}
              onClick={() => onNavigate?.("leagues")}
            />
          </section>

          <section className="watchwicket-featured" aria-labelledby="featured-match-title">
            <div className="watchwicket-featured__heading">
              <h2 id="featured-match-title">
                {featuredCategory === "live" && <span className="watchwicket-live-dot" />}
                {featuredCategory === "live" ? "Live Match"
                  : featuredCategory === "upcoming" ? "Upcoming Match"
                  : featuredCategory === "completed" ? "Completed Match"
                  : "Featured Match"}
              </h2>
              <button type="button" onClick={handleViewAll}>View All <ArrowIcon /></button>
            </div>
            {loading ? (
              <div className="watchwicket-featured__empty">Loading your matches...</div>
            ) : featuredCategory === "live" && liveMatch ? (
              <LiveCard match={liveMatch} score={liveScore} onOpen={handleOpenFeatured} />
            ) : featuredCategory === "upcoming" && upcomingMatch ? (
              <UpcomingCard match={upcomingMatch} now={now} onOpen={handleOpenFeatured} />
            ) : featuredCategory === "completed" && completedMatch ? (
              <CompletedCard match={completedMatch} onOpen={handleOpenFeatured} />
            ) : (
              <div className="watchwicket-featured__empty">
                <div className="watchwicket-featured__empty-icon"><CricketIcon /></div>
                <div>
                  <strong>No matches yet</strong>
                  <p>Your live, upcoming and recent matches will appear here.</p>
                </div>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}

function DashboardCard({ title, description, image, tone, icon, onClick }: { title: string; description: ReactNode; image: string; tone: "blue" | "green" | "gold" | "purple"; icon: ReactNode; onClick?: () => void }) {
  return (
    <button type="button" className={`watchwicket-dashboard-card watchwicket-dashboard-card--${tone}`} onClick={onClick}>
      <img src={image} alt="" aria-hidden="true" />
      <span className="watchwicket-dashboard-card__shade" />
      <span className="watchwicket-dashboard-card__icon">{icon}</span>
      <span className="watchwicket-dashboard-card__copy"><strong>{title}</strong><small>{description}</small></span>
      <span className="watchwicket-dashboard-card__arrow"><ArrowIcon /></span>
    </button>
  );
}

function TeamColumn({ name, logo, score, overs, align = "left" }: { name: string; logo: string | null; score?: string; overs?: string; align?: "left" | "right" }) {
  return (
    <div className={`ww-team-col ww-team-col--${align}`}>
      <TeamLogo name={name} logo={logo} />
      <span className="ww-team-col__name">{name || "Team"}</span>
      {score !== undefined && <strong className="ww-team-col__score">{score}</strong>}
      {overs !== undefined && overs !== "" && <span className="ww-team-col__overs">{overs}</span>}
    </div>
  );
}

function TeamLogo({ name, logo }: { name: string; logo: string | null }) {
  const initials = (name || "?").trim().split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase() || "??";
  return <span className="ww-team-logo">{logo ? <img src={logo} alt="" /> : initials}</span>;
}

function InfoLine({ match }: { match: SummaryRow }) {
  const fmt = getFormatLabel(match.matchData?.format || (match as any).format);
  const venue = match.venue || match.matchData?.matchLocation || match.matchData?.venue || null;
  const parts: string[] = [];
  if (fmt) parts.push(fmt);
  if (venue) parts.push(venue);
  if (parts.length === 0) return null;
  return <p className="ww-info-line">{parts.join(" • ")}</p>;
}

function VsDivider({ label = "VS" }: { label?: string }) {
  return <div className="ww-vs"><span>{label}</span></div>;
}

function LiveBadge({ crr }: { crr: string }) {
  return (
    <div className="ww-live-badge" aria-label="Live match">
      <span className="ww-live-badge__dot" />
      <strong>LIVE</strong>
      <small>CRR {crr}</small>
    </div>
  );
}

function LiveCard({ match, score, onOpen }: { match: SummaryRow; score: { score: string; overs: string; crr: string }; onOpen: () => void }) {
  const md = match.matchData;
  const idx = typeof md?.currentInnings === "number" ? md.currentInnings : 0;
  const currentInn = md?.innings?.[idx];
  const battingTeam = currentInn?.battingTeam;
  const aScore = battingTeam === "A" ? score.score : undefined;
  const aOvers = battingTeam === "A" ? `(${score.overs})` : undefined;
  const bScore = battingTeam === "B" ? score.score : undefined;
  const bOvers = battingTeam === "B" ? `(${score.overs})` : undefined;
  return (
    <button type="button" className="ww-match-card" onClick={onOpen}>
      <InfoLine match={match} />
      <div className="ww-match-card__teams">
        <TeamColumn name={match.teamAName} logo={match.teamALogoUrl} score={aScore} overs={aOvers} />
        <div className="ww-match-card__center">
          <LiveBadge crr={score.crr} />
        </div>
        <TeamColumn name={match.teamBName} logo={match.teamBLogoUrl} score={bScore} overs={bOvers} align="right" />
      </div>
    </button>
  );
}

function formatCountdown(target: string, now: Date): string {
  const d = new Date(target);
  if (isNaN(d.getTime())) return "";
  const diff = d.getTime() - now.getTime();
  if (diff <= 0) return "Starting soon";
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Starting now";
  if (mins < 60) return `Starts in ${mins}m`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours < 24) return `Starts in ${hours}h ${remMins}m`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return `Starts in ${days}d ${remHours}h`;
}

function formatScheduled(target: string | null): string {
  if (!target) return "Time TBC";
  const d = new Date(target);
  if (isNaN(d.getTime())) return "Time TBC";
  return d.toLocaleString(undefined, {
    weekday: "short", day: "numeric", month: "short",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

function UpcomingCard({ match, now, onOpen }: { match: SummaryRow; now: Date; onOpen: () => void }) {
  const when = formatScheduled(match.scheduledAt);
  const countdown = match.scheduledAt ? formatCountdown(match.scheduledAt, now) : "";
  return (
    <button type="button" className="ww-match-card" onClick={onOpen}>
      <InfoLine match={match} />
      <div className="ww-match-card__teams">
        <TeamColumn name={match.teamAName} logo={match.teamALogoUrl} />
        <div className="ww-match-card__center"><VsDivider /></div>
        <TeamColumn name={match.teamBName} logo={match.teamBLogoUrl} align="right" />
      </div>
      <div className="ww-match-card__schedule">
        <span className="ww-match-card__when">{when}</span>
        {countdown && <span className="ww-match-card__countdown">{countdown}</span>}
      </div>
    </button>
  );
}

function CompletedCard({ match, onOpen }: { match: SummaryRow; onOpen: () => void }) {
  const result = getMatchResultText(match as any);
  const firstInnings = match.matchData?.innings?.[0];
  const secondInnings = match.matchData?.innings?.[1];
  const aScore = firstInnings ? `${firstInnings.totalRuns ?? 0}/${firstInnings.wickets ?? 0}` : undefined;
  const bScore = secondInnings ? `${secondInnings.totalRuns ?? 0}/${secondInnings.wickets ?? 0}` : undefined;
  return (
    <button type="button" className="ww-match-card" onClick={onOpen}>
      <InfoLine match={match} />
      <div className="ww-match-card__teams">
        <TeamColumn name={match.teamAName} logo={match.teamALogoUrl} score={aScore} />
        <div className="ww-match-card__center"><VsDivider /></div>
        <TeamColumn name={match.teamBName} logo={match.teamBLogoUrl} score={bScore} align="right" />
      </div>
      <p className="ww-match-card__result">{result}</p>
    </button>
  );
}

function MenuIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h16" /></svg>; }
function ArrowIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>; }
function CalendarIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18" /></svg>; }
function ClockIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>; }
function WeatherIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 18h10a4 4 0 0 0 .4-7.98A5.5 5.5 0 0 0 7 12.5 3 3 0 0 0 7 18Z" /><path d="M7 7V4M4.5 6l2-2M2 10h3" /></svg>; }
function CalendarCheckIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="5" width="16" height="16" rx="3" /><path d="M8 3v4M16 3v4M4 10h16M8 15l2 2 5-5" /></svg>; }
function TeamIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="9" r="3" /><circle cx="17" cy="10" r="2.5" /><path d="M3 20a6 6 0 0 1 12 0M15 16a5 5 0 0 1 6 4" /></svg>; }
function ChartIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 20V11M12 20V5M19 20v-8" /><path d="M3 20h18" /></svg>; }
function TrophyIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 4h8v5a4 4 0 0 1-8 0V4ZM12 13v5M8 21h8M5 5H3v2a4 4 0 0 0 4 4M19 5h2v2a4 4 0 0 1-4 4" /></svg>; }
function CricketIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m16 4 4 4-9 9-4-4 9-9Z" /><path d="m5 15-2 6 6-2M14 6l4 4" /></svg>; }
